import { FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import { Icon } from 'common-components';
import styles from './monthPicker.module.scss';
import { MonthPickerProps, MonthProps } from './monthPickerTypes';
import Tooltip from '../../tooltip';
import { useTranslationContext } from '../../contexts/translationContext';

const MonthPicker: FC<MonthPickerProps> = (props: MonthPickerProps) => {
    const { yearsAmount = 10, onRangePicked, initialStartTimes } = props;
    const { translate } = useTranslationContext();
    const currentDate = new Date();

    const populateYears = () => {
        const yearsArr: number[] = [];
        const date = new Date();
        for (let i = 0; i < yearsAmount; i++) {
            const fullYear = date.getFullYear();
            yearsArr.push(fullYear);
            date.setFullYear(date.getFullYear() - 1);
        }
        return yearsArr;
    };

    const [selectedYear, setSelectedYear] = useState<number>(currentDate.getFullYear());

    const populateMonths = () => {
        const monthsArr: MonthProps[] = [];
        const date = new Date(Date.UTC(currentDate.getFullYear(), 0, 1));
        for (let i = 0; i <= 11; i++) {
            const isDisabled = new Date(Date.UTC(selectedYear, i, 1)).getTime() > currentDate.getTime();
            monthsArr.push({
                value: i,
                label: date.toLocaleString('default', { month: 'short' }),
                disabled: isDisabled
            });
            date.setMonth(date.getMonth() + 1);
        }
        for (let i = 0; i <= 3; i++) {
            monthsArr.push({
                value: i,
                label: date.toLocaleString('default', { month: 'short' }),
                disabled: true,
                isPast: true
            });
            date.setMonth(date.getMonth() + 1);
        }
        return monthsArr;
    };

    const years: number[] = populateYears();
    const months: MonthProps[] = populateMonths();

    const [selectedStartTime, setSelectedStartTime] = useState<number>(0);
    const [selectedEndTime, setSelectedEndTime] = useState<number>(0);
    const [yearsBatchIndex, setYearsBatchIndex] = useState<number>(0);
    const YEARS_SLICE = 4;

    useEffect(() => {
        setSelectedStartTime(initialStartTimes?.startTimeMS || 0);
        setSelectedEndTime(initialStartTimes?.endTimeMS || 0);
    }, [initialStartTimes]);

    const MAX_DATE_MS = new Date(Date.UTC(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)).setHours(
        23,
        59,
        59,
        999
    );

    const boxMonthStyle = month => {
        const year = month.isPast ? selectedYear - 1 : selectedYear;
        const msTime = new Date(Date.UTC(year, month.value, 1)).getTime();
        if (msTime == selectedStartTime || msTime == selectedEndTime) {
            return styles['selected-box'];
        }
        if (selectedStartTime && selectedEndTime && msTime < selectedEndTime && msTime > selectedStartTime) {
            return styles['picked-box'];
        }
        if (month.disabled) {
            return styles['disabled-box'];
        }
        return '';
    };

    const boxYearStyle = year => {
        if (selectedYear == year) {
            return styles['selected-year-box'];
        }
        const startTimeYear = selectedStartTime ? new Date(selectedStartTime).getFullYear() : null;
        const endTimeYear = selectedEndTime ? new Date(selectedEndTime).getFullYear() : null;
        if (
            year == startTimeYear ||
            year == endTimeYear ||
            (startTimeYear && endTimeYear && year > startTimeYear && year < endTimeYear)
        ) {
            return styles['picked-year-box'];
        }
        return '';
    };

    const onMonthClick = month => {
        const msTime = new Date(Date.UTC(selectedYear, month.value, 1)).getTime();
        if (month.disabled || msTime > MAX_DATE_MS) {
            return;
        }
        if (!selectedStartTime || msTime < selectedStartTime) {
            setSelectedStartTime(msTime);
            setSelectedEndTime(0);
        } else if (!selectedEndTime) {
            setSelectedEndTime(msTime);
            onRangePicked?.(selectedStartTime, msTime);
        } else {
            setSelectedStartTime(msTime);
            setSelectedEndTime(0);
        }
    };

    const onIconClick = index => {
        setYearsBatchIndex(yearsBatchIndex + index);
    };

    const tooltipTitle = month => {
        const msTime = new Date(Date.UTC(selectedYear, month.value, 1)).getTime();
        if (msTime == selectedStartTime) {
            return translate('From');
        }
        if (msTime == selectedEndTime) {
            return translate('Until');
        }
        return '';
    };

    return (
        <div className={styles['month-picker-container']} data-testid="month-picker-container">
            <div className={styles['years-container']}>
                <div
                    className={styles['icon-container']}
                    onClick={() => onIconClick(-1)}
                    style={{ marginRight: '.6rem', visibility: yearsBatchIndex > 0 ? 'visible' : 'hidden' }}
                >
                    <Icon name={'chevronLeft'} className={styles.icon} />
                </div>
                <div className={styles['years-splice-container']}>
                    {years.slice(YEARS_SLICE * yearsBatchIndex, YEARS_SLICE * (yearsBatchIndex + 1)).map(year => {
                        return (
                            <span
                                key={year}
                                className={classNames(styles['box-container'], boxYearStyle(year))}
                                onClick={() => setSelectedYear(year)}
                            >
                                {year}
                            </span>
                        );
                    })}
                </div>

                <div
                    className={styles['icon-container']}
                    onClick={() => onIconClick(1)}
                    style={{
                        marginLeft: '.6rem',
                        visibility: YEARS_SLICE * (yearsBatchIndex + 1) < years.length ? 'visible' : 'hidden'
                    }}
                >
                    <Icon name={'chevronRight'} className={styles.icon} />
                </div>
            </div>
            <div className={styles['months-container']}>
                {months.map((month, index) => {
                    return (
                        <Tooltip
                            key={index}
                            title={tooltipTitle(month)}
                            placement={'top'}
                            type={'secondary'}
                            extraclasses={{ marginBottom: '0px !important' }}
                        >
                            <div
                                key={index}
                                className={classNames(styles['box-container'], boxMonthStyle(month))}
                                onClick={() => onMonthClick(month)}
                            >
                                <span className={month.disabled ? styles['label-secondary'] : styles['label-primary']}>
                                    {month.label}
                                </span>
                            </div>
                        </Tooltip>
                    );
                })}
            </div>
        </div>
    );
};

export default MonthPicker;
